import React, { useState } from "react";
import { useDialog } from "../../utils/useDialog";

/**
 * "Cancel this appointment?", asked before a patient cancels a visit.
 *
 * DESIGN_SYSTEM.md: a flat card (white, 10px radius, Charcoal-10% border, 24px
 * padding, no shadow); a destructive confirmation in Error red; the way out as a
 * ghost button that says what it does ("Keep appointment", never a bare
 * "Back"); 44px targets. The Sage focus ring is the global :focus-visible rule
 * in design-system.css, so no outline is set here. It replaces a Material
 * dialog in Material blue with a drop shadow, a bare "Back" and an all-caps
 * "CANCEL" that read as "cancel this dialog".
 *
 * Keyboard (useDialog): focus starts on "Keep appointment", the safe choice;
 * Tab stays inside; Escape keeps the appointment.
 */
const formatWhen = (start) => {
  if (!start) return null;
  const d = new Date(start);
  if (Number.isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${date} at ${time}`;
};

const buttonBase = {
  minHeight: "44px",
  padding: "0 20px",
  // Side by side when there is room; stacked full width on a phone.
  flex: "1 1 160px",
  borderRadius: "var(--radius)",
  fontFamily: "inherit",
  fontSize: "0.875rem",
  fontWeight: 500,
  cursor: "pointer",
  transition: "transform 150ms ease-out, background-color 150ms ease-out",
};

const CancelAppointmentDialog = ({ open, appointment, busy, onKeep, onConfirm }) => {
  const panelRef = useDialog(open, busy ? undefined : onKeep);
  const [hovered, setHovered] = useState(null);

  if (!open) return null;

  const when = formatWhen(appointment?.apptStartTime);
  const what = [appointment?.diagName && `${appointment.diagName} visit`, appointment?.doctorName && `with ${appointment.doctorName}`]
    .filter(Boolean)
    .join(" ");

  const press = (name) => ({
    onMouseEnter: () => setHovered(name),
    onMouseLeave: () => setHovered(null),
  });

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onKeep(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        backgroundColor: "rgba(46, 46, 46, 0.5)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "16px",
      }}
    >
      <div
        ref={panelRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="cancel-appointment-title"
        aria-describedby="cancel-appointment-detail"
        tabIndex={-1}
        style={{
          backgroundColor: "var(--youro-card)",
          color: "var(--youro-charcoal)",
          border: "1px solid var(--youro-border)",
          borderRadius: "var(--radius)",
          padding: "24px",
          width: "100%",
          maxWidth: "480px",
          boxShadow: "none",
          textAlign: "left",
        }}
      >
        {/* Not an h2/h3: Patient-Homepage.css forces heading sizes with !important. */}
        <p
          id="cancel-appointment-title"
          role="heading"
          aria-level={2}
          style={{ margin: "0 0 8px", fontFamily: "Nunito, sans-serif", fontSize: "1.25rem", fontWeight: 700, lineHeight: 1.3 }}
        >
          Cancel this appointment?
        </p>
        <p id="cancel-appointment-detail" style={{ margin: "0 0 24px", fontSize: "1rem", lineHeight: 1.5 }}>
          {what || when
            ? <>Your {what || "appointment"}{when ? <> on <strong>{when}</strong></> : null} will be cancelled, and we'll let your provider know.</>
            : <>This appointment will be cancelled, and we'll let your provider know.</>}
          {" "}You can book a new time whenever you're ready.
        </p>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={onKeep}
            disabled={busy}
            {...press("keep")}
            style={{
              ...buttonBase,
              border: "none",
              color: "var(--youro-charcoal)",
              backgroundColor: hovered === "keep" && !busy ? "var(--youro-mint)" : "transparent",
              opacity: busy ? 0.5 : 1,
              cursor: busy ? "default" : "pointer",
            }}
          >
            Keep appointment
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            aria-busy={busy || undefined}
            {...press("cancel")}
            style={{
              ...buttonBase,
              border: "none",
              color: "#FFFFFF",
              backgroundColor: "var(--youro-error)",
              opacity: busy ? 0.5 : 1,
              cursor: busy ? "default" : "pointer",
              transform: hovered === "cancel" && !busy ? "scale(1.02)" : "none",
            }}
          >
            {busy ? "Cancelling…" : "Cancel appointment"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CancelAppointmentDialog;
